import React, { Component, type ReactElement } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import { Radio as AkRadio } from '@atlaskit/radio';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { SingleVersion } from '@atlassian/jira-version-picker/src/common/types.tsx';
import type { ReleaseVersionValidationErrors, ReleasableVersion } from '../../../model/index.tsx';
import messages from './messages.tsx';

export type RenderVersionPicker = (arg1: {
	onSelected: (arg1: SingleVersion | undefined, arg2: UIAnalyticsEvent | undefined) => void;
}) => ReactElement;

export type Props = {
	readonly toDoInProgressIssuesCount: number;
	readonly isSaving: boolean;
	readonly shouldSwapIssues: boolean;
	readonly errors: ReleaseVersionValidationErrors;
	readonly renderVersionPicker: RenderVersionPicker;
	readonly onInputChange: (arg1: Partial<ReleasableVersion>) => void;
	readonly onSwapIssuesSelected: (arg1: boolean) => void;
	readonly intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class SwapIssues extends Component<Props> {
	onSwapIssues = (version?: SingleVersion) => {
		if (version) {
			this.inputChange({ moveUnfixedIssuesTo: version.id });
		} else {
			this.inputChange({ moveUnfixedIssuesTo: '' });
		}
	};

	onMoveIssuesSelected = () => {
		const { onSwapIssuesSelected } = this.props;
		onSwapIssuesSelected(true);
	};

	onRemoveVersionFromIssuesSelected = () => {
		const { onSwapIssuesSelected } = this.props;
		onSwapIssuesSelected(false);
	};

	inputChange(update: Partial<ReleasableVersion>) {
		const { onInputChange } = this.props;
		onInputChange({ moveUnfixedIssuesTo: update.moveUnfixedIssuesTo });
	}

	render() {
		const {
			intl: { formatMessage },
			toDoInProgressIssuesCount,
			shouldSwapIssues,
			errors,
			isSaving,
			renderVersionPicker,
		} = this.props;
		const { moveUnfixedIssuesTo: moveUnfixedIssuesToError } = errors;

		return (
			<Field
				name="UnresolvedIssue"
				isRequired
				label={formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.unresolvedIssuesHeaderIssueTermRefresh
						: messages.unresolvedIssuesHeader,
					{
						issues: toDoInProgressIssuesCount,
					},
				)}
			>
				{() => (
					<>
						<AkRadio
							isDisabled={isSaving}
							isChecked={shouldSwapIssues}
							onChange={this.onMoveIssuesSelected}
							label={formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.unresolvedIssuesMoveIssueTermRefresh
									: messages.unresolvedIssuesMove,
								{
									issues: toDoInProgressIssuesCount,
								},
							)}
							testId="version-modals.release-version-modal.content.swap-issues.move-radio"
						/>
						<VersionPickerContainer>
							{renderVersionPicker({ onSelected: this.onSwapIssues })}
						</VersionPickerContainer>
						{moveUnfixedIssuesToError && (
							<ValidationError>
								{formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.invalidVersionChosenIssueTermRefresh
										: messages.invalidVersionChosen,
									{
										issues: toDoInProgressIssuesCount,
									},
								)}
							</ValidationError>
						)}
						<AkRadio
							isDisabled={isSaving}
							isChecked={!shouldSwapIssues}
							onChange={this.onRemoveVersionFromIssuesSelected}
							label={formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.unresolvedIssuesIgnoreIssueTermRefresh
									: messages.unresolvedIssuesIgnore,
							)}
							testId="version-modals.release-version-modal.content.swap-issues.ignore-radio"
						/>
					</>
				)}
			</Field>
		);
	}
}

export default injectIntl(SwapIssues);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VersionPickerContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `0 ${gridSize * 12}px ${token('space.100', '8px')} ${token('space.300', '24px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValidationError = styled.div({
	marginTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.danger', colors.R200),
});
